import { graphql } from 'gatsby';
import React, { FC, useEffect, useState } from 'react';

import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  Value,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import { NextIcon, PreviousIcon, RefreshIcon } from '@/components/icons';
import { useIsEnabledForOperator, useTranslate } from '@/contexts';
import { isAccessDeniedError, optInStatusLabel } from './helpers';
import { useOptInHistory, useOptInStatus } from './usePlayerJackpotOptIn';

export const Fragment = graphql`
  fragment SanityPlayerJackpotOptInHistoryBlock on SanityPlayerJackpotOptInHistoryBlock {
    title {
      ...SanityLocaleString
    }
    sourceCodes
    enabledByOperator {
      ...SanityOperator
    }
  }
`;

const StatusValue: FC<{
  title: string;
  sourceCode?: string;
  refreshCount: number;
}> = ({ title, sourceCode, refreshCount }) => {
  const { status, fetching, error, refresh } = useOptInStatus(sourceCode);

  useEffect(() => {
    if (refreshCount) {
      refresh({ requestPolicy: 'network-only' });
    }
  }, [refreshCount, refresh]);

  return (
    <Value
      title={title}
      fetching={fetching}
      error={Boolean(error)}
      value={optInStatusLabel(status)}
    />
  );
};

const PlayerJackpotOptInHistoryContent: FC<{
  block: Queries.SanityPlayerJackpotOptInHistoryBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const [refreshCount, setRefreshCount] = useState(0);
  const overall = useOptInStatus();

  const sourceCodes = (block.sourceCodes ?? []).filter((code): code is string =>
    Boolean(code),
  );

  const {
    history,
    fetching,
    error,
    sourceCode,
    nextPage,
    previousPage,
    refresh,
    desc,
    defaultFilter,
    updateFilter,
  } = useOptInHistory();

  const accessDenied =
    isAccessDeniedError(overall.error) || isAccessDeniedError(error);

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'playerJackpotOptInHistory',
    data: history,
    fetching,
    setOrderBy: () => {},
    desc,
    defaultFilter,
    updateFilter,
    accessDenied,
    schema: (s) => [
      s.dateTimeValue({
        field: 'processedAt',
        title: 'Processed At',
        fromFilterField: 'from',
        toFilterField: 'to',
      }),
      s.booleanValue({
        field: 'optedIn',
        title: 'Opted In',
        filterField: 'optedIn',
      }),
      s.initiatorValue({
        field: 'initiator',
        title: 'Initiator',
      }),
      s.selectValue({
        field: 'sourceCode',
        title: 'Source Code',
        filterField: 'sourceCode',
        hidden: true,
        options: sourceCodes.map((code) => ({ label: code, value: code })),
        getValue: () => sourceCode ?? 'All',
      }),
    ],
  });

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      options={
        !accessDenied && (
          <CardOptions>
            <CardOptionsButton
              disabled={!previousPage}
              onClick={() => previousPage && previousPage()}
            >
              <PreviousIcon />
            </CardOptionsButton>
            <CardOptionsButton
              disabled={!nextPage}
              onClick={() => nextPage && nextPage()}
            >
              <NextIcon />
            </CardOptionsButton>
            <GlobalCardOptions />
            <CardOptionsButton
              onClick={() => {
                overall.refresh({ requestPolicy: 'network-only' });
                refresh({ requestPolicy: 'network-only' });
                setRefreshCount((count) => count + 1);
              }}
            >
              <RefreshIcon />
            </CardOptionsButton>
          </CardOptions>
        )
      }
    >
      <CardBody>
        {accessDenied ? (
          <p className="p-3 text-gray-500">
            You don't have access to jackpot opt-in data.
          </p>
        ) : (
          <>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              <Value
                title="Overall"
                fetching={overall.fetching}
                error={Boolean(overall.error)}
                value={optInStatusLabel(overall.status)}
              />
              {sourceCodes.map((code) => (
                <StatusValue
                  key={code}
                  title={code}
                  sourceCode={code}
                  refreshCount={refreshCount}
                />
              ))}
            </div>
            <p className="px-3 pb-3 text-sm text-gray-500">
              Very recent changes may take a few seconds to appear.
            </p>
            <DataGrid />
          </>
        )}
      </CardBody>
    </Card>
  );
};

// Gate in a wrapper so the opt-in queries never fire for disabled operators.
const PlayerJackpotOptInHistoryBlock: FC<{
  block: Queries.SanityPlayerJackpotOptInHistoryBlockFragment;
}> = ({ block }) => {
  const { isEnabled } = useIsEnabledForOperator(block.enabledByOperator);

  return isEnabled ? <PlayerJackpotOptInHistoryContent block={block} /> : null;
};

export default PlayerJackpotOptInHistoryBlock;
